import Vue, { provide } from 'vue';
import VueApollo from 'vue-apollo';
import VueMask from 'v-mask';
import axios from 'axios';
import { ApolloClients } from '@vue/apollo-composable';
import * as Sentry from '@sentry/vue';
import defaultProvider, { onLogout } from '@/vue-apollo';
import nexusProvider from '@/apollo/nexusProvider';
import { ValidationObserver, ValidationProvider } from '@/validation';
import {
  getAccessToken,
  getRefreshToken,
  setRefreshToken,
  getStorageMode,
  isAuthenticated,
  getItem as secureGetItem
} from '@/services/secureStorage/secureStorage';
import { MeQuery, AuthRefreshMutation } from '@/generated/nexus-graphql';
import { onNexusLogin } from '@/apollo/nexusAuth';
import { ME_QUERY, REFRESH_MUTATION } from '@/graphql';
import { startTokenRefreshTimer } from '@/utils/tokenRefreshTimer';
import {
  installPreviewFetchGuard,
  isPreviewPath,
  previewAxiosInterceptor,
  scrubPreviewTokens
} from '@/utils/previewGuard';

import datadogRum from './tracer';
import { gtmService } from './services/analytics/gtm';

import App from './App.vue';
import RootErrorBoundary from './components/RootErrorBoundary.vue';
import { appCssReady } from '@/utils/appCssReady';
import { clearEnhancedAuthState } from './utils/auth-state-persistence';
import DefaultLayout from './layouts/Default.vue';
import NoNavLayout from './layouts/NoNav.vue';
// import './registerServiceWorker';
// eslint-disable-next-line import/no-named-as-default
import router from './router';
import store from './store';
import vuetify from './plugins/vuetify';
import '@mdi/font/css/materialdesignicons.css';
import './styles/tailwind.css';

// Initialize Heap Analytics if configured
declare global {
  interface Window {
    heap: any;
  }
}

// PP-3: Heap sends the page path as a GET parameter and has no beforeSend hook, so a
// preview page load (signed token in the path) does not boot it at all.
if (
  import.meta.env.VITE_HEAP_ID &&
  typeof window !== 'undefined' &&
  !isPreviewPath(window.location.pathname)
) {
  window.heap = window.heap || [];
  window.heap.load = function loadHeap(e: string, t?: any) {
    window.heap.appid = e;
    window.heap.config = t || {};
    const r = document.createElement('script');
    r.type = 'text/javascript';
    r.async = true;
    r.src = `https://cdn.heapanalytics.com/js/heap-${e}.js`;
    const a = document.getElementsByTagName('script')[0];
    a.parentNode?.insertBefore(r, a);
    const methods = [
      'addEventProperties',
      'addUserProperties',
      'clearEventProperties',
      'identify',
      'resetIdentity',
      'removeEventProperty',
      'setEventProperties',
      'track',
      'unsetEventProperty'
    ];
    for (let i = 0; i < methods.length; i++) {
      window.heap[methods[i]] = function heapMethod(...args: any[]) {
        window.heap.push([methods[i]].concat(args));
      };
    }
  };
  window.heap.load(import.meta.env.VITE_HEAP_ID);
}

// Initialize Google Tag Manager if configured. PP-11: not on a preview page load - GTM's
// page_view carries page_location, which is the signed token, and nothing on a preview
// page is worth tracking (same rule as Heap above).
if (typeof window !== 'undefined' && !isPreviewPath(window.location.pathname)) {
  gtmService.initialize();
}

// By default, do NOT send cross-site cookies with every request.
// This avoids CORS "No Allow Credentials" errors when hitting our
// micro-services from different domains. If a specific call really
// needs cookies you can still pass { withCredentials: true } ad-hoc.
axios.defaults.withCredentials = false;

// Services that should NOT receive Bearer token authentication
// (they use different auth mechanisms like AWS IAM or are public)
const EXCLUDED_FROM_BEARER_AUTH = ['/proxy/program-schema-service'];

// Helper function to check if URL is a PilotCity service that needs Bearer auth
const isPilotCityService = (url: string): boolean => {
  if (!url) return false;

  // Handle relative URLs that use the /proxy/ prefix (these are proxied to PilotCity services via vite.config.ts)
  if (url.startsWith('/proxy/')) {
    // Check if this proxy path is excluded from Bearer auth
    const isExcluded = EXCLUDED_FROM_BEARER_AUTH.some(excluded => url.startsWith(excluded));
    if (isExcluded) {
      return false;
    }
    return true;
  }

  try {
    const urlObj = new URL(url);
    const hostname = urlObj.hostname.toLowerCase();

    // Check for PilotCity domains, AWS API Gateway, and localhost (for development)
    return (
      hostname.includes('pilotcity') ||
      (hostname.includes('execute-api') && hostname.includes('amazonaws.com')) ||
      hostname === 'localhost' ||
      hostname === '127.0.0.1' ||
      hostname.startsWith('192.168.') ||
      hostname.endsWith('.local')
    );
  } catch (error) {
    // If URL parsing fails, fall back to string matching
    return url.toLowerCase().includes('pilotcity') || url.toLowerCase().includes('execute-api');
  }
};

// PP-3: while a program preview is open, every REST write is refused here and every
// raw fetch write in the wrapper; inert otherwise (a flag check per request).
axios.interceptors.request.use(previewAxiosInterceptor);
installPreviewFetchGuard();

// Add request interceptor to automatically include Bearer token for PilotCity services only
axios.interceptors.request.use(
  function onFulfilled(config) {
    const token = getAccessToken();
    const url = config.url || '';

    // Only add Authorization header for PilotCity services
    if (token && isPilotCityService(url)) {
      return {
        ...config,
        headers: {
          ...(config.headers || {}),
          Authorization: `Bearer ${token}`
        }
      };
    }
    return config;
  },
  function onRejected(error) {
    return Promise.reject(error);
  }
);

console.log(
  `Axios configured with PilotCity domain-restricted token interceptor (Storage mode: ${getStorageMode()})`
);

Sentry.init({
  Vue,
  dsn:
    import.meta.env.VITE_SENTRY_DSN ||
    'https://6ca6c10052b5e5f66b3968221d65b694@o528452.ingest.us.sentry.io/4509136628875264',
  // Setting this option to true will send default PII data to Sentry.
  // For example, automatic IP address collection on events
  sendDefaultPii: true,
  environment: process.env.NODE_ENV,
  // Tie events to a release so source maps map cleanly + we can spot per-deploy regressions.
  release: import.meta.env.VITE_SENTRY_RELEASE || undefined,
  // Capture a sample of performance traces (was unset → 0 → no perf data).
  tracesSampleRate: 0.15,
  // Drop expected/unactionable events at the source so the autofix backlog stays signal-rich.
  // Conservative: only auth rejections (401/403) — NEVER 400/500 (those can be real bugs).
  beforeSend(event) {
    const v = event.exception?.values?.[0]?.value || event.message || '';
    if (
      /status code 40[13]\b|UnauthorizedError|\bUnauthorized\b|\bForbidden\b|Token unavailable after/i.test(
        v
      )
    ) {
      return null;
    }
    // PP-3: request.url, breadcrumbs and transaction names carry the preview token.
    return scrubPreviewTokens(event);
  },
  beforeBreadcrumb: breadcrumb => scrubPreviewTokens(breadcrumb),
  // Ignore unactionable errors from CEFSharp (.NET embedded browsers)
  // Updated regex to prevent catastrophic backtracking (code review optimization)
  ignoreErrors: [
    /^Non-Error promise rejection captured with value: Object Not Found Matching Id:\d+, MethodName:[^,]*,\s*ParamCount:\d+$/,
    // Vue Router non-errors (expected guard redirects / redundant navigation) — not bugs.
    'NavigationDuplicated',
    /Avoided redundant navigation to current location/,
    /Redirected when going from/,
    // Browser layout noise, never actionable.
    /ResizeObserver loop/
  ],
  integrations: [
    Sentry.replayIntegration(),
    Sentry.feedbackIntegration({
      // Button & Form Text
      triggerLabel: 'Report a Bug',
      formTitle: 'Help Us Improve PilotCity',
      submitButtonLabel: 'Send Report',
      cancelButtonLabel: 'Cancel',
      nameLabel: 'Full Name',
      // namePlaceholder: 'Your Name',
      emailLabel: 'Email Address',
      // emailPlaceholder: 'your.email@example.com',
      messageLabel: 'What went wrong?',
      // messagePlaceholder: 'Please describe what happened and what you expected to happen...',
      // successMessageText: 'Thanks! We\'ll look into this right away.',

      // Requirements
      isNameRequired: true,
      isEmailRequired: true,
      showName: true,
      showEmail: true,

      // Features
      // enableScreenshot: false,
      showBranding: false,
      colorScheme: 'system',

      // Custom colors for light theme
      // themeLight: {
      //   background: '#ffffff',
      //   backgroundHover: '#f5f5f5',
      //   foreground: '#1a1a1a',
      //   border: '#e0e0e0',
      //   accentBackground: '#6366f1',
      //   accentForeground: '#ffffff',
      //   success: '#10b981',
      //   error: '#ef4444',
      // },

      // Custom colors for dark theme
      // themeDark: {
      //   background: '#1a1a1a',
      //   backgroundHover: '#2a2a2a',
      //   foreground: '#ffffff',
      //   border: '#3a3a3a',
      //   accentBackground: '#818cf8',
      //   accentForeground: '#ffffff',
      //   success: '#10b981',
      //   error: '#ef4444',
      // },

      // Callbacks for analytics/tracking
      onFormOpen: () => {
        console.log('User opened feedback form');
      },
      onSubmitSuccess: () => {
        console.log('Feedback submitted successfully');
      },
      onSubmitError: (error: any) => {
        console.error('Feedback submission failed:', error);
      }
    })
  ],
  // Session Replay
  replaysSessionSampleRate: 0.1, // 10% of sessions (was 100% — high cost); errors still get full replay below.
  replaysOnErrorSampleRate: 1.0 // Always capture a replay for sessions where an error occurs.
});
// Replay recording observes every DOM mutation; starting it during boot put that work inside the
// landing's first seconds on slow phones. It starts once the page has loaded (load speed 2026-09-14).
const startReplay = () => window.setTimeout(() => datadogRum.startSessionReplayRecording(), 3000);
if (document.readyState === 'complete') startReplay();
else window.addEventListener('load', startReplay, { once: true });

Vue.config.productionTip = false;

Vue.component('DefaultLayout', DefaultLayout);
Vue.component('NoNavLayout', NoNavLayout);
Vue.component('ValidationProvider', ValidationProvider);
Vue.component('ValidationObserver', ValidationObserver);

Vue.use(VueMask);

Vue.use(VueApollo);

// COMMENTED OUT: This interceptor was causing forced logouts on school WiFi
// due to network issues (captive portals, proxies, timeouts) that return 401/403
// responses. Individual API calls should handle their own auth errors appropriately.
//
// axios.interceptors.response.use(
//   function onFulfilled(response) {
//     return response;
//   },
//   function onRejected(error) {
//     if (error.response?.status === 401) {
//       onLogout();
//       router.push('/');
//     }
//     return Promise.reject(error);
//   }
// );

// Create a combined provider that includes both clients
// Nexus is the default for new functionality, Realm is kept for existing features during migration
const combinedProvider = new VueApollo({
  defaultClient: nexusProvider.defaultClient, // Make Nexus the default for new development
  clients: {
    realm: defaultProvider.defaultClient, // Keep Realm client for existing functionality
    nexus: nexusProvider.defaultClient // Nexus client for new functionality
  }
});

// Bootstrap Vuex user state before the app mounts
async function bootstrapUser() {
  if (isAuthenticated()) {
    try {
      const { data } = await nexusProvider.defaultClient.query<MeQuery>({
        query: ME_QUERY,
        fetchPolicy: 'network-only'
      });
      if (data && data.me) {
        const { me } = data;
        const userPayload = {
          _id: me.dbUser?.id || '',
          userId: me.userId,
          email: me.email,
          firstName: me.firstName,
          lastName: me.lastName,
          fullName: me.fullName,
          profileImg: me.profileImg,
          profile: (me as any).profile,
          shareCode: me.shareCode,
          userTypes: me.userTypes || [],
          stripeConnectId: me.stripeConnectId || null,
          stripeVerified: me.stripeVerified || null,
          slug: (me.dbUser as any)?.slug || null,
          portfolioPublic: (me.dbUser as any)?.portfolioPublic ?? null
        };
        await store.dispatch('user/loginUser', userPayload);
      }
    } catch (err) {
      console.warn('Initial Me query failed, attempting token refresh...', err?.message || err);

      // Attempt silent refresh using cookies via authRefresh mutation

      try {
        const { data: refreshData } = await nexusProvider.defaultClient.mutate<AuthRefreshMutation>(
          {
            mutation: REFRESH_MUTATION,
            fetchPolicy: 'no-cache'
          }
        );

        const authRef = refreshData?.authRefresh;
        if (authRef?.ok && authRef.accessToken) {
          // Update Apollo auth header & localStorage
          await onNexusLogin(authRef.accessToken);
          if (authRef.refreshToken) setRefreshToken(authRef.refreshToken);

          // Retry Me query to hydrate user
          const { data: retryData } = await nexusProvider.defaultClient.query<MeQuery>({
            query: ME_QUERY,
            fetchPolicy: 'network-only'
          });
          if (retryData?.me) {
            const { me } = retryData;
            const userPayload = {
              _id: me.dbUser?.id || me.userId || '',
              userId: me.userId,
              email: me.email,
              firstName: me.firstName,
              lastName: me.lastName,
              fullName: me.fullName,
              profileImg: me.profileImg,
              profile: (me as any).profile,
              shareCode: me.shareCode,
              userTypes: me.userTypes || [],
              stripeConnectId: me.stripeConnectId || null,
              stripeVerified: me.stripeVerified || null,
              slug: (me.dbUser as any)?.slug || null,
              portfolioPublic: (me.dbUser as any)?.portfolioPublic ?? null
            };
            await store.dispatch('user/loginUser', userPayload);
            return;
          }
        }
        console.warn('Token refresh failed or returned no token. Logging out.');
        onLogout();
      } catch (refreshErr) {
        console.error('Token refresh mutation failed', refreshErr);
        onLogout();
      }
    }
  } else if (getRefreshToken()) {
    // Access token expired but refresh token exists — attempt silent refresh.
    // This covers the case where the user returns to the app (or refreshes the
    // page) after the 15-min access token expired but within the 30-day refresh
    // window (fixes blank /monitor screen — Sarina Ko issue).
    console.log('🔄 Access token expired, attempting refresh with existing refresh token...');
    try {
      const { data: refreshData } = await nexusProvider.defaultClient.mutate<AuthRefreshMutation>({
        mutation: REFRESH_MUTATION,
        fetchPolicy: 'no-cache'
      });
      const authRef = refreshData?.authRefresh;
      if (authRef?.ok && authRef.accessToken) {
        await onNexusLogin(authRef.accessToken);
        if (authRef.refreshToken) setRefreshToken(authRef.refreshToken);
        const { data: retryData } = await nexusProvider.defaultClient.query<MeQuery>({
          query: ME_QUERY,
          fetchPolicy: 'network-only'
        });
        if (retryData?.me) {
          const { me } = retryData;
          const userPayload = {
            _id: me.dbUser?.id || me.userId || '',
            userId: me.userId,
            email: me.email,
            firstName: me.firstName,
            lastName: me.lastName,
            fullName: me.fullName,
            profileImg: me.profileImg,
            profile: (me as any).profile,
            shareCode: me.shareCode,
            userTypes: me.userTypes || [],
            stripeConnectId: me.stripeConnectId || null,
            stripeVerified: me.stripeVerified || null,
            slug: (me.dbUser as any)?.slug || null,
            portfolioPublic: (me.dbUser as any)?.portfolioPublic ?? null
          };
          await store.dispatch('user/loginUser', userPayload);
          startTokenRefreshTimer();
        }
      } else {
        console.warn('Token refresh returned no token. Logging out.');
        onLogout();
      }
    } catch (refreshErr) {
      console.warn(
        'Refresh with existing refresh token failed:',
        refreshErr?.message || refreshErr
      );
      onLogout();
    }
  }

  // Ghost user cleanup: if no valid tokens exist but a stale user remains in
  // localStorage, clear it to prevent "Welcome back!" bootloops. This handles
  // the case where bootstrapUser() did nothing (both token checks failed) but
  // the user key persists from a previous session.
  if (!isAuthenticated() && !getRefreshToken() && secureGetItem('user')) {
    console.warn('🧹 Ghost user detected (no tokens but user in storage). Cleaning up.');
    onLogout();
  }
}

// ----------------------------------------------------------------------------
// Start the application after optional user bootstrap
// ----------------------------------------------------------------------------
(async () => {
  // Never block the initial mount on the auth bootstrap. bootstrapUser() awaits the
  // `Me` query + token-refresh retries against Nexus; if that hangs (Nexus still
  // booting right after `yarn start`, a stalled network, or an extension holding the
  // request) or throws, the app must still render. We race it against a short timeout
  // and swallow rejections — bootstrapUser keeps running in the background and hydrates
  // auth into the store when it finally resolves; until then the public/logged-out
  // shell renders instead of a permanent blank page. This is the root-cause fix for the
  // "blank right after starting the servers" bug.
  const BOOT_AUTH_TIMEOUT_MS = 6000;
  /* The timer has to be CLEARED when the bootstrap wins the race. Promise.race
     settles on the first to finish, but the loser keeps running - so the timeout
     fired and logged on every page load in production, including anonymous ones
     that painted in half a second, announcing a six-second auth stall that never
     happened. Measured on prod: /connect logged out paints at 528ms and still
     printed the warning at ~6s. A console that cries wolf on every load costs
     real time the day something is actually wrong. */
  let bootTimer: number | undefined;
  await Promise.race([
    bootstrapUser().catch((err: unknown) => {
      // eslint-disable-next-line no-console
      console.error('[boot] bootstrapUser failed — mounting app anyway:', err);
    }),
    new Promise<void>(resolve => {
      bootTimer = window.setTimeout(() => {
        bootTimer = undefined; // it fired; nothing left to cancel
        // eslint-disable-next-line no-console
        console.warn(
          `[boot] auth bootstrap exceeded ${BOOT_AUTH_TIMEOUT_MS}ms — mounting now; auth will hydrate when it resolves.`
        );
        resolve();
      }, BOOT_AUTH_TIMEOUT_MS);
    })
  ]);
  if (bootTimer !== undefined) window.clearTimeout(bootTimer);

  // Start proactive token refresh if user is authenticated
  if (isAuthenticated()) {
    startTokenRefreshTimer();
  }

  // Never mount before the entry stylesheet is on the page (utils/appCssReady): the build
  // makes that stylesheet non-render-blocking so the /guide shell paints first, and whether
  // a module script waits for it differs by engine (WebKit did not - unstyled app, 2026-09-14).
  await appCssReady();

  // Signed-out program landing: download the landing's chunk alongside the router's
  // ParticipantGuide chunks instead of after the program document arrives (it was requested
  // 1.3 s after the data on Slow 4G). Same module id as ParticipantGuide's async component,
  // so the later import() resolves from this download.
  if (!isAuthenticated() && /^\/guide\/[a-f0-9]{24}\/\d+/i.test(window.location.pathname)) {
    import('@/views/Guide/components/ProgramLanding.vue').catch(() => undefined);
  }

  const vm = new Vue({
    apolloProvider: combinedProvider,
    setup() {
      // Provide Apollo clients for composition API
      provide(ApolloClients, {
        default: nexusProvider.defaultClient, // Default to Nexus for new functionality
        realm: defaultProvider.defaultClient, // Realm client for existing functionality
        nexus: nexusProvider.defaultClient // Nexus client for new functionality
      });
    },
    router,
    store,
    vuetify,
    // Wrap App in a root error boundary so a stale-client-state throw during boot
    // renders a one-click recovery screen instead of unmounting to a blank page.
    render: h => h(RootErrorBoundary)
  }).$mount('#app');

  // Expose for Cypress tests
  if (typeof window !== 'undefined') {
    (window as any).__app__ = vm;
  }

  // Blank-page watchdog (backstop for the SILENT case: nothing threw for the error
  // boundary to catch, yet the app rendered nothing). If #app is empty a few seconds
  // after boot, recover ONCE by clearing the stale client auth state and reloading onto
  // the clean logged-out path. A one-shot sessionStorage flag guarantees it can never
  // loop; on a healthy render the flag is cleared so a future bad-state event can recover.
  if (typeof window !== 'undefined') {
    const RECOVERY_FLAG = 'pc_blank_recovery_attempted';
    window.setTimeout(() => {
      const el = document.getElementById('app');
      const isBlank = !el || el.childElementCount === 0 || el.innerText.trim().length === 0;
      if (!isBlank) {
        try {
          sessionStorage.removeItem(RECOVERY_FLAG);
        } catch (e) {
          /* ignore */
        }
        return;
      }
      if (sessionStorage.getItem(RECOVERY_FLAG)) return; // already tried once — never loop
      try {
        sessionStorage.setItem(RECOVERY_FLAG, '1');
      } catch (e) {
        /* ignore */
      }
      // eslint-disable-next-line no-console
      console.error(
        '[BlankWatchdog] App rendered empty after boot — clearing stale auth state and reloading once.'
      );
      try {
        localStorage.removeItem('apollo-token');
      } catch (e) {
        /* ignore */
      }
      try {
        clearEnhancedAuthState();
      } catch (e) {
        /* ignore */
      }
      window.location.reload();
    }, 4000);
  }
})();
